import type { CompanyAggregateRequest, CompanyAggregateResponse } from '@/components/reports/lists/types';

const ANALYTICS_COMPANIES_URL = 'https://api.pricingsaas.com/functions/v1/api/analytics/companies';
const ANALYTICS_API_KEY = import.meta.env.VITE_ANALYTICS_API_KEY;

export async function fetchAnalyticsCompanies(
  request: CompanyAggregateRequest
): Promise<CompanyAggregateResponse> {
  // Check if API key is configured
  if (!ANALYTICS_API_KEY) {
    throw new Error('Analytics API key not configured. Please add VITE_ANALYTICS_API_KEY to your .env file.');
  }

  try {
    const response = await fetch(ANALYTICS_COMPANIES_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${ANALYTICS_API_KEY}`,
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      // Try to parse error response
      let errorMessage = 'Failed to fetch analytics companies';
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // If parsing fails, use status text
        errorMessage = `${errorMessage}: ${response.statusText}`;
      }

      // Handle specific status codes
      if (response.status === 401 || response.status === 403) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 429) {
        throw new Error('Rate limit exceeded. Please try again later.');
      } else if (response.status >= 500) {
        throw new Error('Analytics service is temporarily unavailable. Please try again later.');
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data as CompanyAggregateResponse;
  } catch (error) {
    // Re-throw our custom errors
    if (error instanceof Error) {
      throw error;
    }

    // Handle network errors
    throw new Error('Failed to connect to analytics service. Please check your internet connection.');
  }
}
